import { InnerBox } from "@/components/elements/InnerBox";
import { YEAR_MONTH_DAY_STR_FORMAT } from "@/constants/datetime";
import { TalentInfoItem } from "@/features/talent/components/talent-detail/TalentInfoItem";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black, text, state } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { Stack, Box, Typography, Grid } from "@mui/material";
import { dayjs } from "@/utils/dayjs";
import { ATTACHED_DOCUMENT_COLOR } from "@/types/enum";
import { useGetLanguageTestList } from "@/api/services/global/master";
import { EMPTY_STR } from "@/constants/string";
import { TalentFileItem } from "@/features/talent/components/talent-detail/TalentFileItem";
import { useMemo } from "react";
import { IconErrorOutline } from "@/assets/icons";

type TTalentLanguageAbilityProps = {
  defaultValues?: Partial<TTalentForm>;
};

export const TalentLanguageAbility: FCC<TTalentLanguageAbilityProps> = ({ defaultValues }) => {
  const { languageTestLevelOptions } = useGetLanguageTestList();

  const getColor = (color: string) => {
    return ATTACHED_DOCUMENT_COLOR[color as keyof typeof ATTACHED_DOCUMENT_COLOR];
  };

  const specificBusinessClassificationsStr = defaultValues?.specificBusinessClassifications
    ?.map((item) => item?.companySpecificBusinessClassificationName)
    .filter(Boolean)
    .join("、");

  const languageTestDisplayStr = useMemo(() => {
    if (
      !defaultValues?.languageTestLevelIds ||
      !Array.isArray(defaultValues.languageTestLevelIds) ||
      defaultValues.languageTestLevelIds.length === 0
    ) {
      return defaultValues?.languageTestLevelIdsStr ?? EMPTY_STR.TEXT;
    }

    const labels = defaultValues.languageTestLevelIds
      .map((id) => {
        if (id === null || id === undefined) return null;
        const option = languageTestLevelOptions.find((opt) => String(opt.value) === String(id));
        return option?.label ?? null;
      })
      .filter((label): label is string => label !== null);

    return labels.length > 0 ? labels.join("、") : EMPTY_STR.TEXT;
  }, [defaultValues?.languageTestLevelIds, defaultValues?.languageTestLevelIdsStr, languageTestLevelOptions]);

  const isNatTestSelected = useMemo(() => {
    if (
      !defaultValues?.languageTestLevelIds ||
      !Array.isArray(defaultValues.languageTestLevelIds) ||
      defaultValues.languageTestLevelIds.length === 0
    ) {
      return false;
    }
    return defaultValues.languageTestLevelIds.some((id) => {
      if (id === null || id === undefined) return false;
      const selectedOption = languageTestLevelOptions.find((opt) => String(opt.value) === String(id));
      return selectedOption?.label?.includes("NAT-TEST") ?? false;
    });
  }, [defaultValues?.languageTestLevelIds, languageTestLevelOptions]);

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="head20Bold" color={black[30]}>
            分野・日本語能力
          </Typography>
        </Box>
      </Stack>

      <Stack mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TalentInfoItem label="分野" value={defaultValues?.companySpecificIndustry?.companySpecificIndustry} />
          </Grid>

          <Grid item xs={6}>
            <TalentInfoItem label="区分" value={specificBusinessClassificationsStr ?? EMPTY_STR.TEXT} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TalentInfoItem label="日本語能力証明書" value={languageTestDisplayStr}>
              {isNatTestSelected && (
                <Typography variant="cap12" color={black[70]}>
                  <Box
                    component="span"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5, color: state.warning_1, whiteSpace: "nowrap", flexWrap: "nowrap" }}
                  >
                    <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
                      <IconErrorOutline fontSize="20px" />
                    </Box>
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      特定技能日本語能力評価対象外（日本語能力レベル参照）
                    </Box>
                  </Box>
                </Typography>
              )}
            </TalentInfoItem>
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="日本語能力（その他）" value={defaultValues?.languageTestOther ?? EMPTY_STR.TEXT} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TalentInfoItem label="特定技能合格証等" value={defaultValues?.specificSkillCertificateStr} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TalentInfoItem label="職種" value={defaultValues?.major?.majorName} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="作業" value={defaultValues?.jobType?.jobName} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2} direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            証明書など
          </Typography>
        </Box>
      </Stack>
      {defaultValues?.talentUploadFiles?.length ? (
        <Stack>
          {defaultValues?.talentUploadFiles?.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mt: 2,
              }}
            >
              <TalentFileItem
                key={idx}
                currentFileIndex={idx}
                files={defaultValues?.talentUploadFiles}
                filePath={item.filePath}
                previewEnabled
                downloadable={true}
                maxWidth={336}
              />

              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    borderRadius: rounded.md,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: getColor(item?.attachmentDocumentType?.color ?? "")?.bgColor || "#fff",
                    color: getColor(item?.attachmentDocumentType?.color ?? "")?.color || "#000000",
                    border: getColor(item?.attachmentDocumentType?.color ?? "")?.bgColor === "#FFFFFF" ? `1px solid ${black[95]}` : "none",
                    ...typography.cap12,
                    lineHeight: 1,
                    padding: "4px 10px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item?.attachmentDocumentType?.color ? item?.attachmentDocumentType?.documentTypeName : ""}
                </Box>
                <Typography variant="body2" color={text.secondary} sx={{ whiteSpace: "nowrap", pl: 1, ...typography.cap12, color: text.secondary }}>
                  {item.createdAt ? dayjs(item.createdAt).format(YEAR_MONTH_DAY_STR_FORMAT) : ""} 更新
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body14Semi">{EMPTY_STR.TEXT}</Typography>
      )}
    </InnerBox>
  );
};
